import { Box, Flex, Grid, Heading, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, SimpleGrid, Spinner, Text, useDisclosure } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { MdArrowRight } from 'react-icons/md'
import { RiFolderWarningLine } from 'react-icons/ri'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ButtonCmp } from '../../../components/ButtonCmp'
import { SelectCmp } from '../../../components/SelectCmp'
import { ItensTabelaProps, TabelaItens } from '../../../components/TabelaItens'
import { TextoTruncado } from '../../../components/TextoTruncado'
import { CriterioResponse, DescricaoCargo, DescritivoReajusteRequestPut, iAssinaturas, NivelColaborador, NivelCriterioResponse } from '../../../interfaces'
import { appApi } from '../../../services/appApi'
import { Body } from '../../Layouts/Body'
import { Sidebar } from '../Gestao/components/Sidebar'
import { CampoAjuste, consolidarPontos, derivarStatusAjustes, pontosDoBloco } from '../utils/consolidacao'
import { ModalAnaliseConsolidada } from './components/ModalAnaliseConsolidada'

export type { IAlteracoes, IStatusAjustes } from '../utils/ajustes'

type DicionarioNivel = Record<string, string[]>
type CategoriaKey =
  | 'conhecimentosTecnicos'
  | 'habilidades'
  | 'atribuicoes'
  | 'competenciasComportamentais'

export const VisualizarSolicitacoes: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  // Quem abriu pelo painel de assinaturas volta pra ele; o padrão continua sendo a tela do cargo
  const destinoRetorno = (location.state as { voltarPara?: string } | null)?.voltarPara ?? `/Descritivo/Cargos/${id ?? ''}`
  const [Cargo, setCargo] = useState<DescricaoCargo>()
  const [Assinaturas, setAssinaturas] = useState<iAssinaturas[]>([])
  const [Cargos, setCargos] = useState<DescricaoCargo[]>()
  const [Criterios, setCriterios] = useState<CriterioResponse[]>([])

  const solicitacoesPendentes = useMemo(
    () => Cargo?.solicitacaoReajustes?.filter(s => s.statusSolicitacao === 1) ?? [],
    [Cargo]
  )

  const cargosSuperioresIDs = Cargo?.cargosSuperiores?.map(cs => cs.cargoSuperiorId) ?? []
  const cargosSuperiores = Cargos?.filter(c => cargosSuperioresIDs.includes(c.id)) ?? []

  const itensTabelaAtribuicoes = itensPorCategoria(Cargo as DescricaoCargo, 'atribuicoes')
  const itensTabelaConhecimentosTecnicos = itensPorCategoria(Cargo as DescricaoCargo, 'conhecimentosTecnicos')

  const modalAnalise = useDisclosure()
  const modalSucesso = useDisclosure()
  const [enviandoAssinatura, setEnviandoAssinatura] = useState(false)
  const [salvandoSemEnviar, setSalvandoSemEnviar] = useState(false)
  const [campoInicialModal, setCampoInicialModal] = useState<CampoAjuste>()

  function nomePorEmail (email: string): string {
    return Assinaturas.find(a => a.emailAssinante === email)?.nomeAssinante ?? 'Solicitante não identificado'
  }

  const blocos = useMemo(
    () => consolidarPontos(Cargo, solicitacoesPendentes, nomePorEmail),
    [Cargo, solicitacoesPendentes, Assinaturas]
  )

  // Badge e destaque vermelho saem dos mesmos pontos do modal: fontes separadas divergiam
  const statusAjustes = useMemo(() => derivarStatusAjustes(Cargo, blocos), [Cargo, blocos])
  const todosAjustesResolvidos = Object.values(statusAjustes).every(ajuste => ajuste.quantidadeAlteracoes === 0)

  const totalPontos = blocos.flatMap(pontosDoBloco).length

  const solicitantesPendentes = Array.from(new Set(solicitacoesPendentes.map(s => s.emailSolicitante)))

  function itensPorCategoria (cargo: DescricaoCargo, categoria: CategoriaKey): ItensTabelaProps[] {
    if (!cargo?.niveis) return []

    const lista: DicionarioNivel[] = Object.entries(cargo.niveis).map(
      ([nomeNivel, nivel]) => ({
        [nomeNivel]: (nivel[categoria] ?? []).filter(Boolean)
      })
    )

    return [{ descricaoDicionario: lista }]
  }

  function criarNivelVazio (): NivelColaborador {
    return {
      conhecimentosTecnicos: [''],
      habilidades: [''],
      atribuicoes: [''],
      competenciasComportamentais: [''],
      primeiroQuartil: 0,
      media: 0,
      mediana: 0,
      terceiroQuartil: 0,
      criteriosPontuacao: []
    }
  }

  function GetDescritivo (): void {
    appApi.get(`api/CargosESalarios/${id ?? ''}`)
      .then((res) => {
        const data: DescricaoCargo = res.data

        if (Object.keys(data.niveis).length === 0) {
          let niveisGerados: Record<string, NivelColaborador> = {}

          if (data.tipoNivel === 'Júnior/Pleno/Sênior') {
            niveisGerados = {
              Júnior: criarNivelVazio(),
              Pleno: criarNivelVazio(),
              Sênior: criarNivelVazio()
            }
          }
          if (data.tipoNivel === 'I, II e III') {
            niveisGerados = {
              'Nível I': criarNivelVazio(),
              'Nível II': criarNivelVazio(),
              'Nível III': criarNivelVazio()
            }
          }
          if (data.tipoNivel === 'I, II, III, IV e V') {
            niveisGerados = {
              'Nível I': criarNivelVazio(),
              'Nível II': criarNivelVazio(),
              'Nível III': criarNivelVazio(),
              'Nível IV': criarNivelVazio(),
              'Nível V': criarNivelVazio()
            }
          }
          if (data.tipoNivel === 'Único') {
            niveisGerados = {
              Único: criarNivelVazio()
            }
          }

          data.niveis = niveisGerados
        }

        setCargo(data)
      })
      .catch(err => console.log(err))
  }

  function GetAssinaturas (): void {
    appApi.get(`api/Assinatura/ListarAssinaturas/${id ?? ''}`)
      .then((res) => {
        setAssinaturas(res.data)
      })
      .catch(err => console.log(err))
  }

  function GetDescritivos (): void {
    appApi.get('api/CargosESalarios')
      .then((res) => {
        if (res.data.length === 0) {
          setCargos(undefined)
        } else {
          setCargos(res.data)
        }
      })
      .catch(err => console.log(err))
  }

  function GetCriterios (): void {
    appApi.get('api/Criterios')
      .then((res) => {
        setCriterios(res.data)
      })
      .catch(err => console.log(err))
  }

  // Uma requisicao por categoria e em sequencia: cada uma reescreve o cargo inteiro no back
  async function aplicarDecisoes (payloads: DescritivoReajusteRequestPut[]): Promise<void> {
    for (const payload of payloads) {
      await appApi.post('api/CargosESalarios/aceitarSolicitacao?descartar=false', payload)
    }

    GetDescritivo()
  }

  async function salvarSemEnviar (): Promise<void> {
    if (!Cargo) return

    setSalvandoSemEnviar(true)
    await appApi.post('api/CargosESalarios/SalvarSemEnviarParaAssinatura', { CargoId: Cargo.id })
      .then(() => navigate(destinoRetorno))
      .catch(err => console.log(err))
      .finally(() => setSalvandoSemEnviar(false))
  }

  async function liberarParaAssinar (): Promise<void> {
    if (!Cargo) return

    setEnviandoAssinatura(true)
    await appApi.post('api/CargosESalarios/LiberarParaAssinar', { CargoId: Cargo.id })
      .then((res) => {
        if (res.status === 200) {
          modalSucesso.onOpen()
        }
      })
      .catch(err => console.log(err))
      .finally(() => setEnviandoAssinatura(false))
  }

  function abrirAnalise (campo?: CampoAjuste): void {
    setCampoInicialModal(campo)
    modalAnalise.onOpen()
  }

  useEffect(() => {
    // Sem isso a pagina abre com o scroll da tela anterior, em vez de comecar do topo
    window.scrollTo(0, 0)
    GetDescritivo()
    GetAssinaturas()
    GetDescritivos()
    GetCriterios()
  }, [])

  function BotaoAlteracao ({ campo, ajustes }: { campo: CampoAjuste, ajustes: { temAlteracao: boolean, quantidadeAlteracoes: number } }): JSX.Element | null {
    if (!ajustes.temAlteracao) return null

    return (
      <ButtonCmp
        small
        OutlineColor={'Red'}
        leftIcon={<RiFolderWarningLine size={15} />}
        onClick={() => abrirAnalise(campo)}
      >
        {ajustes.quantidadeAlteracoes > 1 ? `Alterações (${ajustes.quantidadeAlteracoes})` : 'Alteração'}
      </ButtonCmp>
    )
  }

  function CartaoIdentificacao ({ titulo, valor, acao }: { titulo: string, valor?: string, acao?: JSX.Element | null }): JSX.Element {
    return (
      <Box border={'1px solid var(--Gray5)'} p={'.875rem 1rem'} minW={'0'}>
        <Flex flexDirection={'column'} gap={'.25rem'}>
          <Flex alignItems={'center'} gap={'.5rem'} flexWrap={'wrap'}>
            <Text fontSize={'12px'} color={'var(--Gray3)'} textTransform={'uppercase'} letterSpacing={'.03em'} fontWeight={'600'}>{titulo}</Text>
            {acao}
          </Flex>
          <TextoTruncado texto={valor ?? '-'} linhas={2} fontSize={'16px'} fontWeight={'600'} color={'var(--Gray1)'} />
        </Flex>
      </Box>
    )
  }

  return (
    <Flex justifyContent={'center'} alignItems={'self-start'} pos={'relative'} gap={'2rem'} margin={'0 auto'} pr={'2rem'}>
      <Flex
        position="sticky"
        top="0"
        left="0"
        direction="column"
        bg="white"
        transition="width 0.3s"
        width={isCollapsed ? '60px' : '220px'}
        height="100vh">
        <Sidebar isCollapsed={isCollapsed} onCollapse={setIsCollapsed} selecionado={'Cargos'} />
      </Flex>
      <Body style={{ width: '100%' }} width={'100%'}>
        <ModalAnaliseConsolidada
          isOpen={modalAnalise.isOpen}
          onClose={modalAnalise.onClose}
          cargo={Cargo}
          criterios={Criterios}
          solicitacoes={solicitacoesPendentes}
          nomePorEmail={nomePorEmail}
          campoInicial={campoInicialModal}
          onAplicar={aplicarDecisoes}
        />

        {/* Modal de sucesso ao liberar para assinatura */}
        <Modal
          isOpen={modalSucesso.isOpen}
          onClose={() => { modalSucesso.onClose(); navigate(destinoRetorno) }}
          isCentered
          size="sm"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Sucesso</ModalHeader>
            <ModalBody>
              <Text>Descritivo liberado para assinatura com sucesso!</Text>
            </ModalBody>
            <ModalFooter>
              <ButtonCmp
                VarColor={'Green2'}
                onClick={() => { modalSucesso.onClose(); navigate(destinoRetorno) }}
              >
                Ok
              </ButtonCmp>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Box display={'flex'} flexDirection={'column'}>
          <Flex
            bg={'white'}
            borderRadius={'.5rem'}
            boxShadow={'var(--SombraBackground)'}
            p={'1.25rem'}
            justifyContent={'space-between'}
            alignItems={'center'}
            gap={'1rem'}
            flexWrap={'wrap'}
            mb={'1.5rem'}
          >
            <Flex flexDirection={'column'} gap={'.25rem'} minW={'0'}>
              <Text fontSize={'12px'} color={'var(--Gray3)'} textTransform={'uppercase'} letterSpacing={'.03em'} fontWeight={'600'}>
                Solicitações de ajuste
              </Text>
              <Heading size={'lg'} color={'var(--Azul)'}>{Cargo?.nomeCargo}</Heading>
              <Text fontSize={'13px'} color={'var(--Gray2)'}>
                {solicitacoesPendentes.length === 0
                  ? 'Nenhuma solicitação pendente'
                  : `${solicitacoesPendentes.length === 1 ? '1 solicitação pendente' : `${solicitacoesPendentes.length} solicitações pendentes`} · ${totalPontos === 1 ? '1 ponto a decidir' : `${totalPontos} pontos a decidir`}`}
              </Text>
              {solicitantesPendentes.length > 0 && (
                <Flex gap={'.375rem'} flexWrap={'wrap'} mt={'.25rem'}>
                  {solicitantesPendentes.map(email => (
                    <Text
                      key={email}
                      fontSize={'12px'}
                      color={'var(--Gray2)'}
                      bgColor={'var(--Gray6)'}
                      borderRadius={'.75rem'}
                      px={'.625rem'}
                      py={'.125rem'}
                    >
                      {nomePorEmail(email)}
                    </Text>
                  ))}
                </Flex>
              )}
            </Flex>

            {totalPontos > 0 && (
              <ButtonCmp big VarColor={'Azul'} onClick={() => abrirAnalise(undefined)}>
                Analisar solicitações
              </ButtonCmp>
            )}
          </Flex>

          <Box pt={'4'} pb={'4'} bg={'white'} p={'1.25rem'} borderRadius={'.5rem'} boxShadow={'var(--SombraBackground)'}>
            <Flex w={'1380px'} border={'1px solid var(--Gray5)'} borderRadius={'.375rem'} overflow={'hidden'} flexDirection={'column'}>
              <Box bg={'var(--Gray6)'} display={'flex'} justifyContent={'center'} alignItems={'center'} h={'42px'} w={'100%'}>
                <Heading size={'md'} color={'var(--Gray1)'}>Identificação</Heading>
              </Box>
              <SimpleGrid columns={3}>
                <CartaoIdentificacao titulo={'Título do cargo'} valor={Cargo?.nomeCargo} />
                <CartaoIdentificacao titulo={'Área'} valor={Cargo?.trilhasDeCarreira?.[0]?.areaNome} />
                <CartaoIdentificacao titulo={'CBO'} valor={Cargo?.cbo} />
                <CartaoIdentificacao titulo={'Hierarquia'} valor={Cargo?.nivelHierarquico} />
                <CartaoIdentificacao titulo={'Atuação'} valor={Cargo?.atuacao} />
                <CartaoIdentificacao
                  titulo={'Cargo do superior imediato'}
                  valor={cargosSuperiores.length > 0
                    ? cargosSuperiores.map(cs => cs.nomeCargo).join(', ')
                    : 'Ninguém (Topo da hierarquia)'}
                  acao={<BotaoAlteracao campo={'cargosSuperiores'} ajustes={statusAjustes.cargosSuperiores} />}
                />
              </SimpleGrid>
              <Box border={'1px solid var(--Gray5)'} p={'.875rem 1rem'}>
                <Flex flexDirection={'column'} gap={'.375rem'}>
                  <Flex alignItems={'center'} gap={'.5rem'} flexWrap={'wrap'}>
                    <Text fontSize={'12px'} color={'var(--Gray3)'} textTransform={'uppercase'} letterSpacing={'.03em'} fontWeight={'600'}>
                      Trilhas de carreira
                    </Text>
                    <BotaoAlteracao campo={'cargosSubordinados'} ajustes={statusAjustes.cargosSubordinados} />
                  </Flex>

                  <Flex direction="column" gap=".5rem" fontSize={'14px'} lineHeight={'18px'}>
                    {Object.entries(Cargo?.cargosFuturos ?? {}).map(([trilha, cargos]) => (
                      <Flex key={trilha} direction="column">
                        <Text fontWeight={'bold'}>{trilha}:</Text>
                        <Flex gap=".25rem" alignItems="center" flexWrap="wrap">
                          {cargos.map((cargoFuturo, idx) => (
                            <Flex key={idx} align="center" gap=".25rem" color="var(--Rosa)" fontWeight="600">
                              {idx > 0 && <MdArrowRight size={16} />}
                              <Text>{cargoFuturo.nome}</Text>
                            </Flex>
                          ))}
                        </Flex>
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
              </Box>
            </Flex>

            <TabelaItens
              tituloTabela={'Missão'}
              isTabelaDescricaoSimples
              descricaoSimples={Cargo?.missao}
              mt={4}
              alteracaoDocumento={statusAjustes.missao.temAlteracao}
              statusAjustes={statusAjustes.missao}
              acaoModalAlteracaoDocumento={() => abrirAnalise('missao')}
            />
            <TabelaItens
              tituloTabela={'Atribuições da função'}
              itensTabela={itensTabelaAtribuicoes}
              mt={4}
              alteracaoDocumento={statusAjustes.atribuicoes.temAlteracao}
              statusAjustes={statusAjustes.atribuicoes}
              acaoModalAlteracaoDocumento={() => abrirAnalise('atribuicoes')}
            />
            <TabelaItens
              tituloTabela={'Conhecimentos técnicos e habilidades'}
              itensTabela={itensTabelaConhecimentosTecnicos}
              mt={4}
              alteracaoDocumento={statusAjustes.requisitosTecnicos.temAlteracao}
              statusAjustes={statusAjustes.requisitosTecnicos}
              acaoModalAlteracaoDocumento={() => abrirAnalise('requisitosTecnicos')}
            />
            <TabelaItens
              tituloTabela={'Requisitos comportamentais'}
              isTabelaDescricaoSimples
              descricaoSimplesArray={Cargo?.competenciasComportamentais}
              statusAjustes={statusAjustes.requisitosComportamentais}
              whiteSpace="pre-line"
              mt={4}
              alteracaoDocumento={statusAjustes.requisitosComportamentais.temAlteracao}
              acaoModalAlteracaoDocumento={() => abrirAnalise('requisitosComportamentais')}
            />

            {Criterios && Cargo?.niveis && (
              <Flex flexDir="column" gap="1rem" w={'1380px'} border={'1px solid var(--Gray5)'} borderRadius={'.375rem'} overflow={'hidden'} boxShadow={'var(--SombraBackground)'} mt={8}>
                <Box bg={'var(--Gray6)'} display={'flex'} justifyContent={'center'} alignItems={'center'} gap={'.75rem'} h={'42px'} w={'100%'}>
                  <Heading size={'md'} color={'var(--Gray1)'}>Critérios</Heading>
                  <BotaoAlteracao campo={'criterios'} ajustes={statusAjustes.criterios} />
                </Box>

                <Flex borderRadius=".25rem" direction="column" border={'1px solid var(--c6)'} boxShadow={'var(--SombraBackground)'} fontSize={'15px'} m={'0 1rem 1rem'}>
                  <Grid gridTemplateColumns={`1.25fr ${Object.entries(Cargo.niveis).map(() => '1fr').join(' ')}`} alignItems={'center'} gap={'1rem'} px="1rem" py=".75rem" borderBottom="1px solid var(--c5)" w={'100%'} fontWeight="700" color={'var(--Gray3)'}>
                    <Flex>Competências</Flex>
                    {Object.keys(Cargo.niveis).map(nivelKey => (
                      <Flex key={nivelKey} flex="1" justify="center">{nivelKey.includes('_') ? nivelKey.replaceAll('_', ' ') : nivelKey}</Flex>
                    ))}
                  </Grid>

                  {Criterios.flatMap((criterio, i) => {
                    const criteriosParaExibir: Array<{ id: string, nome: string, niveis: NivelCriterioResponse[], minimo: boolean }> = [
                      {
                        id: criterio.id,
                        nome: `${criterio.nomeCriterio}${(criterio.niveisMinimos && criterio.niveisMinimos.length > 0) ? ' - Desejável' : ''}`,
                        niveis: criterio.niveis,
                        minimo: false
                      }
                    ]

                    if (criterio.niveisMinimos && criterio.niveisMinimos.length > 0) {
                      criteriosParaExibir.push({
                        id: criterio.id,
                        nome: `${criterio.nomeCriterio} - Mínimo`,
                        niveis: criterio.niveisMinimos,
                        minimo: true
                      })
                    }
                    return criteriosParaExibir.map((crit, j) => (
                      <Grid
                        key={`${crit.id}-${crit.nome}`}
                        gridTemplateColumns={`1.25fr ${Object.entries(Cargo.niveis).map(() => '1fr').join(' ')}`}
                        alignItems="center"
                        gap="1rem"
                        px="1rem"
                        py=".5rem"
                        w="100%"
                        bg={(i + j) % 2 === 0 ? 'var(--c2)' : ''}
                      >
                        <Flex fontWeight="600">{crit.nome}</Flex>

                        {Object.entries(Cargo.niveis).map(([nivelKey, nivel]) => {
                          const lista = nivel.criteriosPontuacao
                          const criterioSelecionado = lista?.find(cp => cp.criterioId === crit.id && crit.minimo === (cp.minimo === true))
                          const foiAlterado = statusAjustes.criterios.alteracoesDicionario?.[nivelKey]?.includes(`${crit.id}|${crit.minimo}`)

                          return (
                            <Flex
                              key={nivelKey}
                              flex="1"
                              border={foiAlterado ? '2px solid' : undefined}
                              borderColor={foiAlterado ? 'var(--Red)' : undefined}
                              borderRadius={foiAlterado ? '.25rem' : undefined}
                            >
                              <SelectCmp
                                somenteLeitura={true}
                                w="100%"
                                placeholder="Selecionar opção"
                                value={criterioSelecionado?.opcaoPontuacaoId ?? ''}
                                OnChange={() => {}}
                              >
                                <option value={''} disabled>Selecione uma opção</option>
                                {crit.niveis.sort((a, b) => b.pontuacao - a.pontuacao).map(n => (
                                  <option key={n.id} value={n.id}>
                                    {n.pontuacao} - {n.nome}
                                  </option>
                                ))}
                              </SelectCmp>
                            </Flex>
                          )
                        })}
                      </Grid>
                    ))
                  })}

                  <Grid gridTemplateColumns={`1.25fr ${Object.entries(Cargo.niveis).map(() => '1fr').join(' ')}`} alignItems={'center'} gap={'1rem'} borderTop="1px solid var(--c5)" px="1rem" py=".75rem" fontWeight="600">
                    <Flex flex="2">Pontuação Total</Flex>
                    {Object.entries(Cargo.niveis).map(([nivelKey, nivel]) => {
                      const total = (nivel.criteriosPontuacao ?? [])
                        .map(cp => {
                          const criterio = Criterios.find(c => c.id === cp.criterioId)
                          const opcao = criterio?.niveis.find(n => n.id === cp.opcaoPontuacaoId)
                          if (criterio?.nivelMinimo && !cp.minimo) return 0
                          return opcao?.pontuacao ?? 0
                        })
                        .reduce((acc, val) => acc + val, 0)

                      return (
                        <Flex key={nivelKey} flex="1" justify="center" fontWeight="700">{total}</Flex>
                      )
                    })}
                  </Grid>
                </Flex>
              </Flex>
            )}

            <Flex justify="center" gap={4} m={6}>
              {/* Funciona como "voltar": as decisoes ja foram aplicadas ao aceitar cada ponto,
              aqui so falta invalidar os codigos antigos e marcar quem tinha assinatura/convite
              valido como Desatualizado. Nao depende de ter resolvido todos os ajustes - serve
              justamente pra sair e voltar depois pra continuar decidindo o que ficou pendente */}
              <ButtonCmp
                big
                OutlineColor={'Green2'}
                isLoading={salvandoSemEnviar}
                onClick={salvarSemEnviar}
              >
                Salvar sem enviar para assinatura
              </ButtonCmp>
              {todosAjustesResolvidos && (
                <ButtonCmp
                  big
                  VarColor={'Green2'}
                  isLoading={enviandoAssinatura}
                  loadingText="Enviando..."
                  spinner={<Spinner size="sm" color="white" />}
                  onClick={liberarParaAssinar}
                >
                  Enviar para assinatura
                </ButtonCmp>
              )}
            </Flex>
          </Box>
        </Box>
      </Body>
    </Flex>
  )
}
